import AdornmentSelect from '@/components/Selects/AdornmentSelect';
import { MULTILINGUAL, SUPPORTED_ELEVENLABS_LANGUAGES_FOR_AGENTS } from '@/constants';
import { isVoiceSupportingLanguage } from '@/hooks/useFilteredVoices';
import { languageToVoicesMap as _languageToVoicesMap } from '@/state/presenters';
import { useAgentEditorStore } from '@/state/root';
import { IVoice, Providers } from '@d-id/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

const Flag = styled.img`
    height: 12px;
    margin-right: 4px;
`;

const Globe = styled.span`
    margin-right: 4px;
    height: 22px;
    font-size: 12px;
    color: black;
`;

const StyledAdornmentSelect = styled(AdornmentSelect<string>)`
    & .MuiInputBase-root {
        padding-top: 3px;
        padding-bottom: 3px;
        padding-left: 16px;
    }

    & .MuiInputBase-input {
        font-size: 12px;
        text-transform: capitalize;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-radius: 8px;
    }
`;

const getStartAdornment = (value: string | null, options: Record<string, IVoice[]>): JSX.Element | null => {
    if (!value || Object.keys(options).length === 0) return null;

    if (value === MULTILINGUAL) {
        return <Globe>🌍</Globe>;
    }

    const code = options[value]?.[0].locale.split('-')?.[1]?.toLowerCase();
    return code ? <Flag loading="lazy" src={`https://flagcdn.com/192x144/${code}.png`} alt={code} /> : null;
};

const getIsIncludesLanguage = (languages: string[], language: string): boolean => {
    return languages.includes(language.split(' ')?.[0]);
};

export const LanguageSelect = observer(() => {
    const agentEditorStore = useAgentEditorStore();

    const languageToVoicesMap = useRecoilValue(_languageToVoicesMap);

    const sortedOptions = useMemo(() => {
        const languages = Object.keys(languageToVoicesMap)
            .sort()
            .filter(lang => {
                const hasNonElevenlabsVoices = languageToVoicesMap[lang].some(voice => voice.provider !== Providers.Elevenlabs);
                if (hasNonElevenlabsVoices) return true;

                return getIsIncludesLanguage(SUPPORTED_ELEVENLABS_LANGUAGES_FOR_AGENTS, lang);
            });
        languages.push(MULTILINGUAL);

        return languages;
    }, [languageToVoicesMap]);

    const handleChange = (_: unknown, value: string | null) => {
        agentEditorStore.setTtsSelectedLanguage(value ?? '');

        if (!value || !agentEditorStore.ttsSelectedVoice) {
            return;
        }

        if (isVoiceSupportingLanguage(agentEditorStore.ttsSelectedVoice, value)) {
            agentEditorStore.setTtsSelectedVoice({ ...agentEditorStore.ttsSelectedVoice, language: value }, false);
            return;
        }

        agentEditorStore.setTtsSelectedVoice(null, false);
        agentEditorStore.setTtsVoiceConfig(prev => ({ ...prev, style: '' }));
        agentEditorStore.setErrors(errors => ({ ...errors, ttsError: null }));
    };

    const value =
        agentEditorStore.ttsSelectedLanguage?.toLowerCase() === MULTILINGUAL.toLowerCase()
            ? MULTILINGUAL
            : agentEditorStore.ttsSelectedLanguage;

    return (
        <StyledAdornmentSelect
            id="language-select"
            label=""
            options={sortedOptions}
            getOptionLabel={option => option?.split('|')?.[0] ?? ''}
            value={value ?? undefined}
            onChange={handleChange}
            getStartAdornment={value => getStartAdornment(value, languageToVoicesMap)}
        />
    );
});
